import { z } from "zod";

const optionalUrl = z.union([
  z.literal(""),
  z.string().url().refine((value) => new URL(value).protocol === "https:", "URL must use HTTPS")
]);
const id = z.string().trim().min(1);
const color = z.string().regex(/^#[0-9a-f]{6}$/i);

const personSchema = z.object({
  fullName: z.string(),
  displayName: z.string(),
  parents: z.string(),
  instagram: z.string(),
  photo: z.string()
});

const storyItemSchema = z.object({
  id,
  date: z.string(),
  title: z.string(),
  description: z.string(),
  image: z.string(),
  order: z.number().int().min(0)
});

const eventSchema = z.object({
  id,
  type: z.string(),
  title: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  timezone: z.string().min(1),
  venueName: z.string(),
  address: z.string(),
  mapsUrl: optionalUrl,
  additionalInformation: z.string(),
  order: z.number().int().min(0)
});

const galleryImageSchema = z.object({
  id,
  url: z.string(),
  alt: z.string(),
  caption: z.string(),
  order: z.number().int().min(0)
});

const bankAccountSchema = z.object({
  id,
  bankName: z.string(),
  accountNumber: z.string(),
  accountHolder: z.string(),
  bankLogo: z.string(),
  order: z.number().int().min(0),
  enabled: z.boolean()
});

const sectionTypeSchema = z.enum([
  "cover",
  "couple",
  "story",
  "countdown",
  "events",
  "rsvp",
  "gallery",
  "gift",
  "liveStreaming",
  "wishes",
  "closing"
]);

const canvasElementLayoutSchema = z.object({
  x: z.number().finite().min(-100).max(100),
  y: z.number().finite().min(-100).max(100),
  scale: z.number().finite().min(0.5).max(2.5),
  fontFamily: z.enum(["inherit", "serif", "sans", "script"]),
  width: z.number().finite().min(5).max(100).optional()
});

export const weddingSchema = z.object({
  project: z.object({
    id,
    name: z.string(),
    slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    status: z.enum(["draft", "published", "archived"]),
    language: z.enum(["id", "en", "zh"]),
    timezone: z.string().min(1),
    createdAt: z.string(),
    updatedAt: z.string(),
    publishedAt: z.string().nullable()
  }),
  invitation: z.object({
    guestName: z.string(),
    title: z.string(),
    subtitle: z.string(),
    openingText: z.string(),
    closingText: z.string(),
    coverImage: z.string(),
    coverVideo: z.string().optional().default(""),
    openButtonText: z.string()
  }),
  couple: z.object({
    groom: personSchema,
    bride: personSchema
  }),
  story: z.object({
    enabled: z.boolean(),
    title: z.string(),
    introduction: z.string(),
    items: z.array(storyItemSchema)
  }),
  events: z.array(eventSchema),
  countdown: z.object({
    enabled: z.boolean(),
    eventId: z.string(),
    targetDate: z.string()
  }),
  gallery: z.object({
    enabled: z.boolean(),
    title: z.string(),
    images: z.array(galleryImageSchema)
  }),
  music: z.object({
    enabled: z.boolean(),
    url: z.string(),
    title: z.string(),
    artist: z.string(),
    autoplayAfterInteraction: z.boolean(),
    loop: z.boolean(),
    volume: z.number().min(0).max(1)
  }),
  rsvp: z.object({
    enabled: z.boolean(),
    title: z.string(),
    description: z.string(),
    deadline: z.string(),
    maximumGuests: z.number().int().min(0).max(100),
    attendanceOptions: z.array(z.string()).min(1)
  }),
  gifts: z.object({
    enabled: z.boolean(),
    title: z.string(),
    description: z.string(),
    backgroundImage: z.string(),
    bankAccounts: z.array(bankAccountSchema),
    qrCodes: z.array(z.string())
  }),
  liveStreaming: z.object({
    enabled: z.boolean(),
    title: z.string(),
    description: z.string(),
    url: optionalUrl,
    thumbnail: z.string(),
    startDate: z.string()
  }),
  wishes: z.object({
    enabled: z.boolean(),
    title: z.string(),
    description: z.string(),
    emojiOptions: z.array(z.string()),
    moderationEnabled: z.boolean()
  }),
  barcode: z.object({
    enabled: z.boolean(),
    title: z.string(),
    description: z.string(),
    image: z.string()
  }),
  closing: z.object({
    image: z.string(),
    title: z.string(),
    message: z.string(),
    signature: z.string()
  }),
  navigation: z.object({
    enabled: z.boolean(),
    items: z.array(z.object({
      id,
      label: z.string(),
      target: z.string(),
      order: z.number().int().min(0)
    }))
  }),
  theme: z.object({
    primaryColor: color,
    secondaryColor: color,
    accentColor: color,
    backgroundColor: color,
    textColor: color,
    headingFont: z.string().min(1),
    bodyFont: z.string().min(1),
    sectionSpacing: z.string().min(1),
    animationStyle: z.enum(["warm", "subtle", "none"])
  }),
  layout: z.object({
    elements: z.record(z.string(), canvasElementLayoutSchema)
  }).optional().default({ elements: {} }),
  sections: z.array(z.object({
    id,
    type: sectionTypeSchema,
    enabled: z.boolean(),
    order: z.number().int().min(0),
    settings: z.record(z.string(), z.unknown())
  })),
  seo: z.object({
    title: z.string(),
    description: z.string(),
    ogImage: z.string(),
    favicon: z.string(),
    noIndex: z.boolean()
  })
});

export type ValidatedWeddingData = z.infer<typeof weddingSchema>;
